import React from 'react';
import { useTranslation } from 'react-i18next';
import { TaskAiOperationItem } from '../types/Task';
import styles from './TaskEditor.module.css';

type TaskAiActivityListProps = {
  items: TaskAiOperationItem[];
  loading: boolean;
  error: string | null;
  formatDate: (iso: string) => string;
};

const formatLabel = (value: string) => value
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, character => character.toUpperCase());

const pluginLabels: Record<string, string> = {
  'task-agent': 'Task agent',
  task: 'Task',
  taskread: 'Task read',
  model: 'Model',
  filecontent: 'File content',
};

const functionLabels: Record<string, string> = {
  'task-agent:run': 'Task agent',
  'model:completion': 'Completion',
  'task:createtask': 'Create task',
  'task:updatetask': 'Update task',
  'task:addtaskcomment': 'Add comment',
  'taskread:gettaskbyid': 'Read task',
  'filecontent:readfilecontent': 'Read attachment',
};

const operationTitle = (item: TaskAiOperationItem) => {
  const plugin = item.pluginName.trim().toLowerCase();
  const functionName = item.functionName.trim().toLowerCase();
  return functionLabels[`${plugin}:${functionName}`]
    ?? formatLabel(item.functionName || item.pluginName || 'Activity');
};

const operationSource = (item: TaskAiOperationItem) => pluginLabels[item.pluginName.trim().toLowerCase()]
  ?? formatLabel(item.pluginName || 'AI');

const formatPayload = (value?: string) => {
  if (!value) return '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const statusClassName = (status: string) => {
  const normalized = status.trim().toLowerCase();
  if (normalized === 'failed' || normalized === 'blocked') return styles.aiActivityStatusFailed;
  if (normalized === 'succeeded') return styles.aiActivityStatusSucceeded;
  return styles.aiActivityStatusNeutral;
};

export const TaskAiActivityList = ({ items, loading, error, formatDate }: TaskAiActivityListProps) => {
  const { t } = useTranslation('tasks');
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  if (loading) {
    return <div className={styles.timelineEmpty}>{t('loadingAiActivity', { defaultValue: 'Loading AI activity...' })}</div>;
  }

  if (error) {
    return <div className={`${styles.timelineEmpty} ${styles.aiActivityError}`}>{error}</div>;
  }

  if (items.length === 0) {
    return <div className={styles.timelineEmpty}>{t('noAiActivity', { defaultValue: 'No AI activity has been logged for this task yet.' })}</div>;
  }

  return (
    <div className={styles.timelineList}>
      {items.map(item => {
        const isExpanded = expandedId === item.id;
        const inputValue = formatPayload(item.inputJson);
        const outputValue = formatPayload(item.outputJson);
        const errorValue = formatPayload(item.error);
        const hasDetails = Boolean(item.operationDetails || inputValue || outputValue || errorValue);

        return (
          <div key={item.id} className={styles.timelineItem}>
            <button
              type="button"
              className={styles.aiActivitySummary}
              onClick={() => setExpandedId(current => current === item.id ? null : item.id)}
              aria-expanded={isExpanded}
            >
              <span className={styles.timelineActor}>{operationTitle(item)}</span>
              <span className={styles.aiActivityMeta}>
                <span className={`${styles.aiActivityStatus} ${statusClassName(item.status)}`}>{formatLabel(item.status || 'Unknown')}</span>
                <span>{operationSource(item)}</span>
                <span title={item.createdAt ? new Date(item.createdAt).toLocaleString() : undefined}>{formatDate(item.createdAt)}</span>
              </span>
            </button>
            {item.operationDetails ? <div className={styles.timelineBody}>{item.operationDetails}</div> : null}
            {isExpanded && hasDetails ? (
              <div className={styles.aiActivityDetails}>
                {errorValue ? (
                  <div>
                    <div className={styles.aiActivityPayloadLabel}>{t('aiActivityError', { defaultValue: 'Error' })}</div>
                    <pre className={styles.aiActivityPayload}>{errorValue}</pre>
                  </div>
                ) : null}
                {inputValue ? (
                  <div>
                    <div className={styles.aiActivityPayloadLabel}>{t('aiActivityInput', { defaultValue: 'Input' })}</div>
                    <pre className={styles.aiActivityPayload}>{inputValue}</pre>
                  </div>
                ) : null}
                {outputValue ? (
                  <div>
                    <div className={styles.aiActivityPayloadLabel}>{t('aiActivityOutput', { defaultValue: 'Output' })}</div>
                    <pre className={styles.aiActivityPayload}>{outputValue}</pre>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
